import { Fields, FormGroups } from 'common/form';
import { TimeFilterType } from 'generated-graphql/types';
import { SourcingExternalSearchFilterFormData } from 'modules/sourcing-external/forms/schemas/sourcingExternalSearchFilterSchema';
import { SOURCING_EXTERNAL_COMPANY_SIZES } from 'modules/sourcing-external/constants/sourcingExternalCompanySizes';
import { SOURCING_EXTERNAL_INDUSTRIES } from 'modules/sourcing-external/constants/sourcingExternalIndustries';
import { SOURCING_EXTERNAL_YEARS_IN_CURRENT_ROLE } from 'modules/sourcing-external/constants/sourcingExternalYearsInCurrentRole';
import { SOURCING_EXTERNAL_YEARS_OF_EXPERIENCE } from 'modules/sourcing-external/constants/sourcingExternalYearsOfExperience';

type GetSourcingExternalSearchFilterFieldsProps = {
  isExcludeCompaniesSelected: boolean;
  isExcludeJobTitlesSelected: boolean;
};

export const getSourcingExternalSearchFilterFields = ({
  isExcludeCompaniesSelected,
  isExcludeJobTitlesSelected
}: GetSourcingExternalSearchFilterFieldsProps): Fields<SourcingExternalSearchFilterFormData> => ({
  jobTitle: {
    type: 'chipInput',
    label: 'Job title',
    subLabel: 'E.g. Senior Analyst. Press ENTER to add another job title.',
    isClearable: true,
    clearLabel: 'job titles',
    group: 'jobTitle'
  },
  currentTitlesOnly: {
    type: 'checkbox',
    checkboxLabel: 'Current job only',
    group: 'jobTitle'
  },
  includeRelatedJobTitles: {
    type: 'checkbox',
    checkboxLabel: 'Include related jobs',
    group: 'jobTitle'
  },
  excludeJobTitles: {
    type: 'checkbox',
    checkboxLabel: 'Exclude job titles',
    group: 'excludeJobTitles'
  },
  jobTitleExclusions: {
    type: 'chipInput',
    subLabel: 'Exclude these job titles from your results. Add all that apply.',
    group: 'excludeJobTitles',
    isClearable: true,
    clearLabel: 'excluded job titles',
    hidden: !isExcludeJobTitlesSelected
  },
  company: {
    type: 'chipInput',
    label: 'Employers',
    subLabel: 'Add all that apply.',
    isClearable: true,
    clearLabel: 'employers',
    group: 'company'
  },
  companyFilter: {
    type: 'radio',
    options: [
      { label: 'Current', value: TimeFilterType.Current },
      { label: 'Past', value: TimeFilterType.Past },
      { label: 'All', value: TimeFilterType.Both }
    ],
    group: 'company'
  },
  excludeCompanies: {
    type: 'checkbox',
    checkboxLabel: 'Exclude companies',
    group: 'excludeCompanies'
  },
  companyExclusions: {
    type: 'chipInput',
    subLabel: 'Exclude these companies from your results. Add all that apply.',
    group: 'excludeCompanies',
    isClearable: true,
    clearLabel: 'excluded companies',
    hidden: !isExcludeCompaniesSelected
  },
  keyword: {
    type: 'text',
    label: 'Keyword',
    subLabel: 'E.g. Founder.'
  },
  location: {
    type: 'chipInput',
    label: 'Location',
    subLabel:
      'Search for a city, region or country. Press ENTER to add another location.',
    isClearable: true,
    clearLabel: 'locations'
  },
  skills: {
    type: 'chipInput',
    label: 'Skills',
    subLabel: 'E.g. financial analysis. Press ENTER to add another skill.',
    isClearable: true,
    clearLabel: 'skills'
  },
  industry: {
    type: 'dropdown',
    label: 'Industry',
    subLabel: 'E.g. banking. Choose all that apply.',
    options: SOURCING_EXTERNAL_INDUSTRIES.map((industry) => ({
      label: industry,
      value: industry
    })),
    isSearchable: true,
    isMulti: true,
    hasList: true,
    isClearable: true,
    clearLabel: 'industries'
  },
  yearsOfExperience: {
    type: 'dropdown',
    label: 'Total years of experience',
    subLabel: 'Choose all ranges that apply.',
    options: SOURCING_EXTERNAL_YEARS_OF_EXPERIENCE.map((experience) => ({
      label: experience.label,
      value: experience.value
    })),
    isMulti: true,
    hasList: true,
    isClearable: true,
    clearLabel: 'experience'
  },
  yearsInCurrentRole: {
    type: 'dropdown',
    label: 'Years in current job',
    subLabel: 'Choose all ranges that apply.',
    options: SOURCING_EXTERNAL_YEARS_IN_CURRENT_ROLE.map((experience) => ({
      label: experience.label,
      value: experience.value
    })),
    isMulti: true,
    hasList: true,
    isClearable: true,
    clearLabel: 'experience'
  },
  companySize: {
    type: 'dropdown',
    label: 'Company size',
    subLabel: 'Choose all that apply.',
    options: SOURCING_EXTERNAL_COMPANY_SIZES.map((size) => ({
      label: size.label,
      value: size.value
    })),
    isMulti: true,
    hasList: true,
    isClearable: true,
    clearLabel: 'company size'
  },
  education: {
    type: 'chipInput',
    label: 'Education',
    subLabel: 'Search for establishments or qualifications.',
    isClearable: true,
    clearLabel: 'education'
  },
  name: {
    type: 'text',
    label: 'Candidate name'
  }
});

export const sourcingExternalSearchFormGroups: FormGroups = {
  jobTitle: {
    sx: {
      direction: 'column'
    }
  },
  company: {
    sx: {
      direction: 'column'
    }
  }
};
